// One KPI CARD on the Signal overview grid (KPI Screen v2, the "Signal" re-layout): the awaiting dot +
// name, the latest recorded figure with its FACTUAL last-two-values delta, the human target with the
// STATED gap (no good/bad tone — the Metric records no direction-of-goodness, G-106), the sparkline over
// the recorded series, the definition statement, and one quiet footer (lifecycle status line + linked-task
// tally). Every rendered fact derives from the projection (G-182); with fewer than two recorded points no
// sparkline draws (P-SELF); no internal id renders (G-197). A proposed Metric renders dashed like a draft
// Task card. Clicking the card OPENS the detail view — the card signals, the detail carries the gates.
// Tokens only (G-181).

import type { MetricCard } from '../projections.ts';
import { Sparkline } from './Sparkline.tsx';
import {
  fmtMetricValue,
  fmtMetricDelta,
  fmtTargetGap,
  isAwaiting,
  metricStatusLine,
} from './metric-format.ts';

export interface KpiCardProps {
  readonly metric: MetricCard;
  readonly onOpen: (id: string) => void;
}

/** The target row: the human target when one is set, with the stated gap once a value exists. */
function TargetRow({ metric }: { readonly metric: MetricCard }): React.JSX.Element {
  if (metric.target === null) {
    return (
      <div className="kc-target">
        <span className="tgt">No target</span>
      </div>
    );
  }
  return (
    <div className="kc-target">
      <span className="tgt">Target {fmtMetricValue(metric.target)}</span>
      {metric.latestValue !== null ? (
        <span className="kc-gap">{fmtTargetGap(metric.latestValue.value, metric.target)}</span>
      ) : null}
    </div>
  );
}

/** A single overview KPI card — one whole-card control that opens this Metric's detail view. */
export function KpiCard({ metric, onOpen }: KpiCardProps): React.JSX.Element {
  const awaiting = isAwaiting(metric);
  const delta = fmtMetricDelta(metric.series);
  return (
    <article className="kpi-card" data-state={metric.state}>
      <button
        type="button"
        className="kpi-open"
        data-open-metric={metric.id}
        onClick={() => onOpen(metric.id)}
        aria-label={`Open KPI: ${metric.name}`}
      >
        <div className="kc-id">
          <span className={awaiting ? 'dot pending' : 'dot'} aria-hidden="true" />
          <span className="kc-name">{metric.name}</span>
        </div>
        <div className="kc-headline">
          <span className="kc-now">
            {metric.latestValue !== null ? fmtMetricValue(metric.latestValue.value) : '—'}
          </span>
          <span className="kc-delta">{delta ?? 'No delta'}</span>
        </div>
        <TargetRow metric={metric} />
        {metric.series.length >= 2 ? (
          <div className="kc-spark">
            <Sparkline series={metric.series} />
          </div>
        ) : null}
        <div className="kc-defn">{metric.definition}</div>
        <div className="kc-foot">
          <span className={awaiting ? 'kc-when pending' : 'kc-when'}>
            {awaiting ? 'Awaiting you' : metricStatusLine(metric)}
          </span>
          <span className="spacer" aria-hidden="true" />
          <span className="kc-tasks">
            {metric.linkedTasks.length > 0
              ? `${metric.linkedTasks.length} ${metric.linkedTasks.length === 1 ? 'task' : 'tasks'}`
              : 'no tasks yet'}
          </span>
        </div>
      </button>
    </article>
  );
}
